import type { ThemeConfig } from 'antd';
import { ipcTokens as t } from './ipcTokens';

/** antd ConfigProvider — 全局 token */
export const designTokens: ThemeConfig['token'] = {
  colorPrimary: t.primary,
  colorPrimaryHover: t.primaryHover,
  colorPrimaryActive: t.primaryActive,
  colorPrimaryBg: t.primaryBg,
  colorPrimaryBgHover: t.primaryBgHover,
  colorPrimaryBorder: t.primary,
  colorPrimaryBorderHover: t.primaryHover,

  colorSuccess: t.success,
  colorSuccessBg: t.successBg,
  colorWarning: t.warning,
  colorWarningBg: t.warningBg,
  colorError: t.destructive,
  colorErrorBg: t.destructiveBg,
  colorInfo: t.primary,
  colorInfoBg: t.primaryBg,

  colorText: t.foreground,
  colorTextSecondary: t.mutedForeground,
  colorTextTertiary: t.mutedForeground,
  colorTextQuaternary: '#94A3B8',
  colorTextHeading: t.foreground,
  colorTextDescription: t.mutedForeground,
  colorTextPlaceholder: t.mutedForeground,
  colorTextDisabled: t.foreground,
  colorTextLabel: t.foreground,

  colorBgLayout: t.background,
  colorBgContainer: t.card,
  colorBgElevated: t.card,
  colorBgSpotlight: t.sidebar,
  colorBgMask: 'rgba(2, 8, 23, 0.45)',
  colorBgContainerDisabled: t.muted,
  colorFillAlter: t.muted,
  colorFillContent: t.muted,
  colorFillContentHover: t.primaryBg,
  colorFillSecondary: t.muted,
  colorFillTertiary: t.muted,

  colorBorder: t.border,
  colorBorderSecondary: t.border,
  colorSplit: t.border,

  colorLink: t.primary,
  colorLinkHover: t.primaryHover,
  colorLinkActive: t.primaryActive,

  fontFamily: t.fontFamily,
  fontSize: t.fontSize,
  fontSizeSM: t.fontSizeSm,
  fontSizeLG: t.fontSizeLg,
  fontSizeXL: t.fontSizeXl,
  fontSizeHeading1: 24,
  fontSizeHeading2: t.fontSizeLg,
  fontSizeHeading3: t.fontSizeLg,
  lineHeight: t.lineHeight,
  lineHeightLG: t.lineHeight,

  borderRadius: t.radiusMd,
  borderRadiusSM: t.radiusSm,
  borderRadiusLG: t.radiusLg,
  controlHeight: t.controlHeight,
  controlHeightLG: t.controlHeightLg,
  controlHeightSM: t.controlHeightSm,

  paddingXS: 4,
  paddingSM: 8,
  padding: 12,
  paddingMD: 16,
  paddingLG: t.contentPadding,
  paddingXL: 24,
  marginXS: 4,
  marginSM: 8,
  margin: 12,
  marginMD: 16,
  marginLG: t.contentPadding,
  marginXL: 24,

  boxShadow: t.shadowPopover,
  boxShadowSecondary: t.shadowPopover,
  boxShadowTertiary: t.shadowCard,
};

/** antd 组件 token — 对齐 UI-STANDARDS-SPEC + 原型 HTML */
export const componentTokens: ThemeConfig['components'] = {
  Table: {
    headerBg: t.muted,
    headerColor: t.mutedForeground,
    headerSplitColor: t.border,
    rowHoverBg: t.rowHoverPrimary,
    rowSelectedBg: t.primaryBg,
    borderColor: t.border,
    fontSize: t.fontSize,
    /* 与 IpcTable 方案1 对齐：padding 10 + line 18 + 边 1 → 含边单行 40 */
    cellPaddingBlock: 10,
    cellPaddingInline: 12,
    cellFontSizeSM: t.fontSizeSm,
    cellPaddingBlockSM: 10,
    cellPaddingInlineSM: 12,
    lineHeight: t.lineHeightSm,
  },
  Menu: {
    darkItemBg: t.sidebar,
    darkItemSelectedBg: t.sidebarAccent,
    darkItemHoverBg: t.sidebarAccent,
    darkSubMenuItemBg: t.sidebar,
    itemBorderRadius: t.radiusMd,
    itemMarginInline: 4,
    itemPaddingInline: 16,
    iconMarginInlineEnd: 8,
    groupTitleFontSize: t.fontSizeSm,
    groupTitleColor: t.mutedForeground,
  },
  Card: {
    paddingLG: t.contentPadding,
  },
  Button: {
    primaryShadow: 'none',
    defaultShadow: 'none',
    dangerShadow: 'none',
    primaryColor: t.primaryForeground,
    fontWeight: 500,
    paddingInline: 16,
    paddingInlineSM: 12,
    paddingInlineLG: 20,
    contentFontSize: t.fontSize,
    contentLineHeight: t.lineHeightSm,
  },
  Modal: {
    titleFontSize: t.fontSize,
    headerBg: t.card,
    contentBg: t.card,
    footerBg: 'transparent',
    paddingContentHorizontal: t.contentPadding,
    paddingMD: t.contentPadding,
  },
  Alert: {
    borderRadiusLG: t.radiusMd,
    withDescriptionPaddingVertical: 12,
    withDescriptionIconSize: 16,
  },
  Progress: {
    remainingColor: t.muted,
    defaultColor: t.primary,
    lineBorderRadius: 999,
  },
  Empty: {
    colorTextDescription: t.mutedForeground,
  },
  Message: {
    contentBg: t.card,
    contentPadding: '10px 14px',
  },
  Tag: {
    defaultBg: t.primaryBg,
    defaultColor: t.primary,
    lineHeight: 20,
  },
  Tabs: {
    horizontalMargin: '0 0 16px 0',
    itemColor: t.mutedForeground,
    itemActiveColor: t.primary,
    itemHoverColor: t.foreground,
    itemSelectedColor: t.primary,
    inkBarColor: t.primary,
    titleFontSize: t.fontSize,
  },
  Form: {
    labelFontSize: t.fontSize,
    labelColor: t.foreground,
    itemMarginBottom: 16,
    verticalLabelPadding: '0 0 6px',
  },
  Input: {
    activeBorderColor: t.ring,
    hoverBorderColor: t.primary,
    activeShadow: '0 0 0 2px rgba(59, 130, 246, 0.2)',
    paddingInline: 12,
    paddingBlock: 6,
    inputFontSize: t.fontSize,
  },
  Select: {
    optionActiveBg: t.muted,
    optionSelectedBg: t.primaryBg,
    optionSelectedColor: t.primary,
  },
  Breadcrumb: {
    fontSize: t.fontSizeSm,
    lastItemColor: t.foreground,
    linkColor: t.mutedForeground,
    linkHoverColor: t.primary,
    separatorColor: t.border,
  },
  Tooltip: {
    colorBgSpotlight: t.sidebar,
    colorTextLightSolid: t.sidebarForeground,
    paddingSM: 8,
    borderRadius: t.radiusSm,
  },
  Popover: {
    paddingLG: 16,
  },
  Switch: {
    colorPrimary: t.primary,
    colorPrimaryHover: t.primaryHover,
  },
  Radio: {
    colorPrimary: t.primary,
  },
  Checkbox: {
    colorPrimary: t.primary,
  },
  DatePicker: {
    activeBorderColor: t.ring,
    hoverBorderColor: t.primary,
    cellHoverBg: t.muted,
    cellActiveWithRangeBg: t.primaryBg,
    cellRangeBorderColor: t.primary,
  },
  Pagination: {
    itemActiveBg: t.primary,
  },
};

export const antdTheme: ThemeConfig = {
  // key 固定，避免 cssVar 未挂上导致禁用色 token 不生效
  cssVar: { prefix: 'ant', key: 'ipc' },
  token: designTokens,
  components: componentTokens,
};
